import type { KeyboardEvent } from 'react'
import cx from 'classnames'
import { NumericInput } from '@/components/inputs/NumericInput'
import {
  durationUnits,
  stepDurationPart,
  toDurationParts,
  withDurationPart,
} from './duration-parts'
import styles from './DurationInput.module.css'

const arrowKeySteps: Partial<Record<string, number>> = {
  ArrowUp: 1,
  ArrowDown: -1,
}

export interface DurationInputProps {
  /**
   * Seconds.
   */
  value: number
  onChange: (seconds: number) => unknown
  isDisabled?: boolean
  isInvalid?: boolean
  testId?: string
}

/**
 * A duration as days, hours and minutes. The arrow keys step a unit, carrying
 * into the next one.
 */
export function DurationInput({
  value,
  onChange,
  isDisabled,
  isInvalid,
  testId,
}: DurationInputProps) {
  const parts = toDurationParts(value)
  const stepOnArrowKey = (unitIndex: number) => (event: KeyboardEvent) => {
    const delta = arrowKeySteps[event.key]
    if (delta && !isDisabled) {
      event.preventDefault()
      onChange(stepDurationPart(value, unitIndex, delta))
    }
  }

  return (
    <div className={cx(styles.root, isDisabled && styles.disabled)}>
      {durationUnits.map((unit, i) => (
        <label key={unit.suffix} className={styles.unit}>
          <NumericInput
            value={parts[i]}
            onChange={(part) => onChange(withDurationPart(value, i, part ?? 0))}
            isDisabled={isDisabled}
            status={isInvalid ? 'danger' : 'default'}
            aria-label={unit.label}
            onKeyDown={stepOnArrowKey(i)}
            testId={testId && `${testId}-${unit.suffix}`}
          />
          <span className={styles.suffix}>{unit.suffix}</span>
        </label>
      ))}
    </div>
  )
}
